/**
 * IntentLiveTicker — real-time pulse of who is engaging with a SKU right now.
 *
 * Polls `/api/intent/live/:productId` every 10 seconds and renders 4 warm
 * counts: watching / deep-focus / cart / checkout. A live emerald dot pulses
 * whenever any count is non-zero. Silent (renders nothing) when nobody is on
 * the page — never adds noise to seller UIs.
 *
 * Sits alongside the buyer table in `IntentRadar.tsx`, and can be reused on
 * any seller-facing PDP.
 */
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, Flame, ShoppingCart, CreditCard, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

interface LiveTicker {
  product_id: string;
  product_title?: string | null;
  watching_now: number;
  deep_focus_now: number;
  cart_watchers: number;
  checkout_watchers: number;
  last_event_at?: string | null;
  last_event_type?: string | null;
  server_time?: string;
}

interface Props {
  productId: string;
  /** Poll interval in ms (default 10s). Lower values increase load. */
  intervalMs?: number;
  className?: string;
}

const REL = {
  s: 1000,
  m: 60_000,
  h: 3_600_000,
};

function _agoLabel(iso?: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  if (diff < 45 * REL.s) return "just now";
  if (diff < 60 * REL.m) return `${Math.max(1, Math.round(diff / REL.m))}m ago`;
  if (diff < 24 * REL.h) return `${Math.round(diff / REL.h)}h ago`;
  return "";
}

export const IntentLiveTicker: React.FC<Props> = ({
  productId,
  intervalMs = 10_000,
  className = "",
}) => {
  const [data, setData] = useState<LiveTicker | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;
    let mounted = true;
    async function fetchOnce() {
      try {
        const { data } = await api.get<LiveTicker>(`/intent/live/${encodeURIComponent(productId)}`);
        if (mounted) { setData(data); setErr(null); }
      } catch (e: any) {
        if (mounted) setErr(e?.response?.data?.detail || "Live ticker unavailable.");
      }
    }
    fetchOnce();
    const t = setInterval(fetchOnce, Math.max(3000, intervalMs));
    return () => { mounted = false; clearInterval(t); };
  }, [productId, intervalMs]);

  if (err) return null;
  if (!data) return null;

  const total = data.watching_now + data.deep_focus_now + data.cart_watchers + data.checkout_watchers;
  if (total === 0) {
    // Muted variant — one soft line so sellers know the ticker is on but idle
    return (
      <div
        className={`rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-2 text-[11px] text-slate-500 flex items-center gap-2 ${className}`}
        data-testid="intent-live-ticker-idle"
      >
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        No live activity on this product right now — polling every {Math.round(intervalMs / 1000)}s
      </div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-transparent dark:from-emerald-950/40 dark:border-emerald-800 p-4 ${className}`}
      data-testid="intent-live-ticker"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="relative inline-flex" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <span className="absolute -inset-1 rounded-full bg-emerald-400/40 animate-ping" />
        </span>
        <span className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-bold">
          Live · Intent Radar
        </span>
        {data.last_event_at && (
          <span className="ml-auto text-[10px] text-slate-500 dark:text-slate-400">
            Last event: {_agoLabel(data.last_event_at)}
          </span>
        )}
      </div>

      {data.product_title && (
        <div className="text-sm font-medium text-slate-900 dark:text-white mb-3 line-clamp-1">
          {data.product_title}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="intent-live-metrics">
        <TickerBox
          icon={<Eye className="h-4 w-4" />}
          label="Watching now"
          hint="last 5 min"
          value={data.watching_now}
          testid="intent-live-watching"
          pulse={data.watching_now > 0}
          color="emerald"
        />
        <TickerBox
          icon={<Flame className="h-4 w-4" />}
          label="Deep focus"
          hint="last 15 min"
          value={data.deep_focus_now}
          testid="intent-live-focus"
          color="orange"
        />
        <TickerBox
          icon={<ShoppingCart className="h-4 w-4" />}
          label="In cart"
          hint="last 24 h"
          value={data.cart_watchers}
          testid="intent-live-cart"
          color="fuchsia"
        />
        <TickerBox
          icon={<CreditCard className="h-4 w-4" />}
          label="At checkout"
          hint="last 24 h"
          value={data.checkout_watchers}
          testid="intent-live-checkout"
          color="rose"
        />
      </div>
    </motion.div>
  );
};

const COLOR_CLS: Record<string, { text: string; dot: string; bg: string }> = {
  emerald: { text: "text-emerald-700 dark:text-emerald-300",  dot: "bg-emerald-500",  bg: "bg-emerald-50 dark:bg-emerald-950/40" },
  orange:  { text: "text-orange-700 dark:text-orange-300",    dot: "bg-orange-500",   bg: "bg-orange-50 dark:bg-orange-950/40" },
  fuchsia: { text: "text-fuchsia-700 dark:text-fuchsia-300",  dot: "bg-fuchsia-500",  bg: "bg-fuchsia-50 dark:bg-fuchsia-950/40" },
  rose:    { text: "text-rose-700 dark:text-rose-300",        dot: "bg-rose-500",     bg: "bg-rose-50 dark:bg-rose-950/40" },
};

const TickerBox: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  value: number;
  testid: string;
  pulse?: boolean;
  color: keyof typeof COLOR_CLS;
}> = ({ icon, label, hint, value, testid, pulse, color }) => {
  const c = COLOR_CLS[color];
  return (
    <div className={`rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-start gap-2`} data-testid={testid}>
      <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${c.bg} ${c.text}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <span className="truncate">{label}</span>
          {pulse && value > 0 && (
            <span className={`relative inline-block h-1.5 w-1.5 rounded-full ${c.dot}`}>
              <span className={`absolute -inset-0.5 rounded-full ${c.dot} opacity-40 animate-ping`} />
            </span>
          )}
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={value}
            initial={{ y: -6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 6, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="text-xl font-semibold tabular-nums text-slate-900 dark:text-white"
          >
            {value.toLocaleString()}
          </motion.div>
        </AnimatePresence>
        <div className="text-[9px] uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-0.5">
          {hint}
        </div>
      </div>
    </div>
  );
};

export default IntentLiveTicker;
